export function calendarDateFromIso(iso: string): Date | null {
  const parts = iso.slice(0, 10).split("-");
  if (parts.length !== 3) return null;
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (!year || month < 1 || month > 12 || day < 1 || day > 31) return null;
  return new Date(year, month - 1, day);
}

export function formatLongDate(iso: string): string {
  const d = calendarDateFromIso(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "long" });
}

export function formatMediumDate(iso: string): string {
  const d = calendarDateFromIso(iso);
  if (!d) return iso;
  return d.toLocaleDateString(undefined, { dateStyle: "medium" });
}

export function daysUntil(iso: string, reference = new Date()): number | null {
  const event = calendarDateFromIso(iso);
  if (!event) return null;
  const start = new Date(reference.getFullYear(), reference.getMonth(), reference.getDate());
  const end = new Date(event.getFullYear(), event.getMonth(), event.getDate());
  return Math.round((end.getTime() - start.getTime()) / 86400000);
}

export function countdownLine(iso: string): string | null {
  const days = daysUntil(iso);
  if (days === null) return null;
  if (days <= 0) return "Today!";
  if (days === 1) return "Tomorrow";
  if (days < 14) return `${days} days away`;
  const weeks = Math.max(Math.floor(days / 7), 1);
  return `${weeks} week${weeks === 1 ? "" : "s"} away`;
}

export function greeting(): string {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 22) return "Good evening";
  return "Hello";
}

export function firstName(displayName: string): string {
  return displayName.split(/\s+/)[0] ?? displayName;
}

export function treeLabel(person: import("../models/types").Person): string {
  if (person.generation === 1) {
    let label = person.display_name.replace(/^(Aunt|Uncle)\s+/i, "");
    if (label === "Annsie") label = "Ann";
    return label;
  }
  return person.display_name;
}

export function isSundiusCousin(person: import("../models/types").Person): boolean {
  return person.generation === 2 && !person.is_partner;
}

export function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export function uuid(): string {
  return crypto.randomUUID();
}

export function minMsPerCard(mode: string): number {
  switch (mode) {
    case "easy":
    case "medium":
    case "hard":
      return 500;
    case "ultra":
    case "everyone":
      return 450;
    case "birth_order_easy":
    case "birth_order_medium":
      return 800;
    case "birth_order_ultra":
      return 600;
    case "family_match":
      return 2000;
    case "family_lore":
    case "whos_with_who":
      return 800;
    default:
      return 500;
  }
}

export function paddedDurationMs(rawMs: number, total: number, mode: string): number {
  const floor = Math.max(1, total) * minMsPerCard(mode);
  return Math.max(rawMs, floor);
}

export function modeTitle(mode: string): string {
  const titles: Record<string, string> = {
    easy: "Easy",
    medium: "All the Cousins",
    hard: "Hard",
    ultra: "Ultra",
    everyone: "Everyone",
    birth_order_easy: "Birth Order · Aunts & Uncles",
    birth_order_medium: "Birth Order · Cousins",
    birth_order_ultra: "Cousins' Kids",
    family_match: "Family Match",
    family_lore: "Family Lore",
    whos_with_who: "Who's With Who",
  };
  return titles[mode] ?? mode;
}

/** Playable boards used for Overall Family Showdown totals (matches iOS). */
export const LEADERBOARD_MODES = [
  "easy",
  "medium",
  "everyone",
  "birth_order_easy",
  "birth_order_medium",
  "birth_order_ultra",
  "family_match",
  "family_lore",
  "whos_with_who",
] as const;

/** Accuracy (floor %) + up to 20 speed bonus on perfect runs — mirrors iOS. */
export function leaderboardPoints(percent: number, durationMs: number): number {
  const accuracy = Math.floor(percent);
  if (accuracy < 100) return accuracy;
  const speedBonus = Math.max(0, 20 - Math.floor(durationMs / 15_000));
  return accuracy + speedBonus;
}

export function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function firstNameOf(displayName: string): string {
  return displayName.trim().split(/\s+/)[0] ?? displayName;
}

export function personalIntroChecks(path: string): [string, string | null] {
  switch (path) {
    case "birthday_phone":
      return ["Your birthday", "Your mobile number"];
    case "birthday_wedding":
      return ["Your birthday", "Your wedding date"];
    case "birthday_state":
      return ["Your birthday", "The state you live in"];
    case "partner_wedding":
      return ["Who claimed you", "Your wedding date"];
    case "partner_state":
      return ["Your person's name", null];
    default:
      return ["A family detail", "One more family detail"];
  }
}

export function personalRemainingChecks(path: string): number {
  return path === "partner_state" ? 1 : 2;
}

export function personalTotalSteps(path: string): number {
  return path === "partner_state" ? 2 : 3;
}
